<script setup lang="ts">
/**
 * An area files can be dropped onto — a file list, a message composer, a
 * page. It wraps its content; while files are dragged over it, an overlay
 * covers the content with an icon, `title` and `hint`. `disabled` keeps the
 * overlay (so the user sees why) but switches it to the „not here“ look and
 * ignores the drop.
 *
 * Emits `files` with the dropped files. The default slot gets `dragActive`.
 * For an area that cannot be wrapped, bind `useFileDrop`'s handlers yourself.
 */
import Ban from 'lucide-vue-next/dist/esm/icons/ban.js'; import Upload from 'lucide-vue-next/dist/esm/icons/upload.js';
import { computed, provide, ref, toRef } from 'vue';
import { useFileDrop } from './useFileDrop';
import { fileDropTargetContext } from './dropTargetContext';

const props = withDefaults(
  defineProps<{
    title?: string;
    hint?: string;
    disabled?: boolean;
    /** Title while disabled („Hier kann nichts abgelegt werden“). */
    disabledTitle?: string;
    /** Overlay inset from the area's edges: `none` covers it edge to edge. */
    inset?: 'none' | 'sm' | 'md';
  }>(),
  {
    title: 'Dateien hier ablegen',
    hint: undefined,
    disabled: false,
    disabledTitle: 'Hier kann nichts abgelegt werden',
    inset: 'sm',
  }
);

const emit = defineEmits<{
  files: [files: File[]];
}>();

const { isDragActive, handlers } = useFileDrop({
  onDrop: (files) => emit('files', files),
  disabled: toRef(props, 'disabled'),
});
const nestedTargets = ref(new Set<symbol>());
provide(fileDropTargetContext, {
  claim(target) {
    if (!nestedTargets.value.has(target))
      nestedTargets.value = new Set([...nestedTargets.value, target]);
  },
  release(target) {
    if (!nestedTargets.value.has(target)) return;
    const next = new Set(nestedTargets.value);
    next.delete(target);
    nestedTargets.value = next;
  },
});
const showOverlay = computed(() => isDragActive.value && nestedTargets.value.size === 0);

function finishDrop() {
  nestedTargets.value = new Set();
}
</script>

<template>
  <div
    class="nx-file-drop-area"
    v-on="handlers"
    @drop.capture="finishDrop"
    @dragend.capture="finishDrop"
  >
    <slot :drag-active="isDragActive" />
    <Transition name="nx-file-drop">
      <div
        v-if="showOverlay"
        class="nx-file-drop-area__overlay"
        :class="[`inset-${props.inset}`, { 'is-disabled': props.disabled }]"
        data-testid="nx-file-drop-overlay"
      >
        <span class="nx-file-drop-area__icon" aria-hidden="true">
          <Ban v-if="props.disabled" class="nxt-icon-lg" />
          <Upload v-else class="nxt-icon-lg" />
        </span>
        <strong class="nx-file-drop-area__title">
          {{ props.disabled ? props.disabledTitle : props.title }}
        </strong>
        <span v-if="props.hint && !props.disabled" class="nx-file-drop-area__hint">
          {{ props.hint }}
        </span>
      </div>
    </Transition>
    <span class="nx-file-drop-area__live" aria-live="polite">
      {{ showOverlay ? (props.disabled ? props.disabledTitle : props.title) : '' }}
    </span>
  </div>
</template>

<style scoped>
.nx-file-drop-area {
  position: relative;
  min-width: 0;
  min-height: 0;
}
.nx-file-drop-area__overlay {
  position: absolute;
  inset: var(--nxt-space-sm);
  z-index: var(--nxt-z-raised);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-md);
  overflow: hidden;
  border: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-border);
  border-radius: var(--nxt-radius-lg);
  background: color-mix(in srgb, var(--nxt-brand-subtle-bg) 88%, transparent);
  backdrop-filter: blur(calc(var(--nxt-hairline) * 3));
  color: var(--nxt-brand-subtle-fg);
  text-align: center;
  pointer-events: none;
}
.nx-file-drop-area__overlay.inset-none {
  inset: 0;
  border-radius: inherit;
}
.nx-file-drop-area__overlay.inset-md {
  inset: var(--nxt-space-md);
}
.nx-file-drop-area__overlay.is-disabled {
  border-color: var(--nxt-border-strong);
  background: color-mix(in srgb, var(--nxt-surface-sunken-bg) 90%, transparent);
  color: var(--nxt-fg-muted);
}
.nx-file-drop-area__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--nxt-control-size-xl);
  height: var(--nxt-control-size-xl);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-md);
  animation: nx-file-drop-bob 1.6s var(--nxt-ease-in-out) infinite;
}
.nx-file-drop-area__overlay.is-disabled .nx-file-drop-area__icon {
  animation: none;
}
.nx-file-drop-area__title {
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-file-drop-area__hint {
  max-width: var(--nxt-panel-width-lg);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-sm);
}
.nx-file-drop-area__live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
.nx-file-drop-enter-active,
.nx-file-drop-leave-active {
  transition:
    opacity var(--nxt-motion-fast) var(--nxt-ease-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-file-drop-enter-from,
.nx-file-drop-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
@keyframes nx-file-drop-bob {
  50% {
    transform: translateY(calc(var(--nxt-hairline) * -4));
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-file-drop-area__icon {
    animation: none;
  }
  .nx-file-drop-enter-active,
  .nx-file-drop-leave-active {
    transition: none;
  }
}
</style>
